import { AnalyticsService } from "@/services/analytics"
import { useEffect } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Icon } from "@iconify/react"
import { TypographySmall } from "@/components/ui/typography"
import { togglePricingPlansDialog } from '@/hooks/usePricingPlans.jsx'
import { getAppBannerSource } from '@/hooks/useAppBanner.jsx'

const BANNER_CONTENT = {
    'ad-spend-limit': {
        message: 'You’ve hit your ad spend cap, so results are limited to your plan allowance',
        showUpgradeButton: true,
    },
    'demo-space': {
        message: 'This is a demo space. All data displayed is mock data',
        showUpgradeButton: false,
    },
    'axon-limited-access': {
        message: 'You have limited access to creative set data. For advanced access, contact us',
        showUpgradeButton: false,
    },
    // Free-tier feature fences. Each is triggered where the limit is hit and shares
    // the existing Upgrade CTA (opens the pricing dialog). See freeTierEntitlements.js.
    'free-report-limit': {
        message: 'You’re seeing your top 20 ads on the Free plan. Upgrade to see all',
        showUpgradeButton: true,
    },
    'free-views-limit': {
        message: 'Creating views and adding widgets is a paid feature. Upgrade to build your own dashboards',
        showUpgradeButton: true,
    },
    'free-reports-limit': {
        message: 'Creating reports is a paid feature. Upgrade to build your own reports',
        showUpgradeButton: true,
    },
    'free-insights-limit': {
        message: 'You’re seeing 1 weekly insight on the Free plan. Upgrade to unlock all of them',
        showUpgradeButton: true,
    },
    'free-watchlist-limit': {
        message: 'Building a watch list is a paid feature. Upgrade to watch advertisers',
        showUpgradeButton: true,
    },
    'free-advertiser-limit': {
        message: 'Showing a preview of this advertiser’s ads. Upgrade to see all of them',
        showUpgradeButton: true,
    },
    'free-creative-limit': {
        message: 'Upgrade to unlock full insights, ideas, comments and tag editing for every creative',
        showUpgradeButton: true,
    },
    'free-chat-limit': {
        message: 'You’re out of AI credits this month. Upgrade for more chat and analysis',
        showUpgradeButton: true,
    }
}

export const AppBanner = ({ type, onClose }) => {

    useEffect(() => {
        if (type) {
            AnalyticsService.track('app-banner-opened', { type })
            // Keep the upgrade funnel complete for the Free-tier fences.
            if (type.startsWith('free-')) {
                AnalyticsService.track('saw_free_limit_banner', { type, source: getAppBannerSource() })
            }
        }
    }, [type])

    const content = BANNER_CONTENT[type]

    return (
        <AnimatePresence>
            {type && content && (
                <motion.div
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -50 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="absolute top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-primary via-primary/90 to-primary backdrop-blur-sm"
                >
                    <div className="relative px-4 py-3">
                        <div className="flex items-center justify-between max-w-7xl mx-auto min-h-10">
                            <div className="flex items-center gap-3 m-auto">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-primary-foreground absolute left-2"
                                    onClick={onClose}
                                >
                                    <Icon icon="solar:close-circle-bold" className="w-6 h-6" />
                                </Button>
                                <div className="flex items-center gap-2">
                                    {/* <Icon icon="solar:megaphone-bold" className="text-primary-foreground text-lg" /> */}
                                    {/* <GradientText className="text-sm font-medium text-primary-foreground"> */}
                                    {/* 🚀 New Feature Available! */}
                                    {/* </GradientText> */}
                                </div>
                                <TypographySmall className="text-primary-foreground">
                                    {content.message}
                                </TypographySmall>
                                {content.showUpgradeButton && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        // className="bg-gradient-to-r from-primary to-secondary text-white hover:shadow-lg hover:shadow-secondary/20 border border-white overflow-hidden"
                                        // className="bg-gradient-to-r from-primary via-secondary to-secondary text-white hover:shadow-lg hover:shadow-secondary/20 border border-white overflow-hidden"
                                        className="bg-secondary text-white hover:shadow-lg hover:shadow-secondary/20 overflow-hidden"
                                        onClick={() => {
                                            AnalyticsService.track('app-banner-upgrade-clicked')
                                            // Reuse the shared upgrade funnel event; source is the
                                            // surface that opened the banner (falls back to the type).
                                            AnalyticsService.track('clicked_upgrade_from_limit', { source: getAppBannerSource() || type })
                                            togglePricingPlansDialog(true)
                                        }}
                                    >
                                        Upgrade
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>

    )
}